import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import CartSidebar from '../components/CartSidebar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Reviews = () => {
  const { isOpen: cartOpen, setIsOpen: setCartOpen } = useCart();
  const [banner, setBanner] = useState(null);
  const [groupedReviews, setGroupedReviews] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/banners`),
      axios.get(`${API}/reviews`),
    ]).then(([bannerRes, reviewRes]) => {
      setBanner(bannerRes.data.find(b => b.type === 'reviews'));
      const reviews = reviewRes.data;
      const grouped = {};
      reviews.forEach(r => {
        const key = r.productId?._id || r.productId;
        const name = r.productId?.name || 'Unknown Product';
        const image = r.productId?.images?.[0] || '';
        if (!grouped[key]) grouped[key] = { name, image, reviews: [] };
        grouped[key].reviews.push(r);
      });
      setGroupedReviews(grouped);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const avgRating = (reviews) => {
    const sum = reviews.reduce((s, r) => s + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="reviews-page">
      <SEO
        title="Customer Reviews & Testimonials"
        description="See what our customers say about Apni Dhani Bakery's cakes, pastries and service. Read real reviews and share your own experience."
        path="/reviews"
      />
      <Navbar onCartClick={() => setCartOpen(true)} />
      <PageBanner banner={banner} />

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="font-playfair text-3xl font-bold text-[#1F1410]">Customer Reviews</h2>
          <p className="text-[#7A6A63] mt-1">Real experiences from our happy customers</p>
          <div className="h-0.5 w-16 bg-[#7A1125] mt-3 rounded" />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : Object.keys(groupedReviews).length === 0 ? (
          <div className="text-center py-20 text-[#7A6A63]">
            <Star size={48} className="mx-auto mb-3 text-[#ECE0DA]" />
            <p className="font-playfair text-lg">No reviews yet</p>
            <p className="text-sm mt-1">Be the first to order and leave a review!</p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="reviews-grouped">
            {Object.entries(groupedReviews).map(([productId, { name, image, reviews }]) => (
              <div key={productId} className="bg-white rounded-2xl border border-[#ECE0DA] overflow-hidden shadow-sm" data-testid={`reviews-product-${productId}`}>
                <div className="flex items-center gap-4 p-5 border-b border-[#ECE0DA]">
                  {image && (
                    <img
                      src={image}
                      alt={name}
                      className="w-14 h-14 rounded-xl object-cover border border-[#ECE0DA]"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <h3 className="font-playfair font-bold text-[#1F1410] text-lg">{name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} size={14} fill={s <= parseFloat(avgRating(reviews)) ? '#F59E0B' : 'none'} stroke={s <= parseFloat(avgRating(reviews)) ? '#F59E0B' : '#D1C4BB'} />
                        ))}
                      </div>
                      <span className="text-sm text-[#7A6A63]">{avgRating(reviews)} · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-[#ECE0DA]">
                  {reviews.map(r => (
                    <div key={r._id} className="p-5" data-testid={`review-${r._id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-[#1F1410] text-sm">{r.customerName}</p>
                          <div className="flex gap-0.5 mt-1">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} size={12} fill={s <= r.rating ? '#F59E0B' : 'none'} stroke={s <= r.rating ? '#F59E0B' : '#D1C4BB'} />
                            ))}
                          </div>
                          {r.comment && <p className="text-[#7A6A63] text-sm mt-2 leading-relaxed">{r.comment}</p>}
                        </div>
                        <span className="text-[#7A6A63] text-xs shrink-0">
                          {new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => {}} />
    </div>
  );
};

export default Reviews;
